import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Heart, Repeat2, Linkedin, Unplug, Send, MessageCircle, Eye } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { PageHeader, Table, StatCard, StatusBadge } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const LinkedInPanel = () => {
  const queryClient = useQueryClient();
  const [orgInput, setOrgInput] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: li } = useQuery({
    queryKey: ["linkedin-status"],
    queryFn: async () => (await api.get("/admin/social/linkedin/status")).data,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get("linkedin");
    if (s === "connected") toast.success("LinkedIn connected — articles will now auto-post to your company page");
    else if (s === "error") toast.error(`LinkedIn connection failed: ${params.get("reason") || "unknown error"}`);
    if (s) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["linkedin-status"] });

  const connect = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/social/linkedin/connect", { origin: window.location.origin });
      window.location.href = res.data.authorization_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const saveOrg = async () => {
    if (!orgInput.trim()) return;
    try {
      const res = await api.post("/admin/social/linkedin/org", { org: orgInput.trim() });
      toast.success(`Company set: ${res.data.org_urn}`);
      setOrgInput("");
      invalidate();
    } catch (e) { toast.error(apiError(e)); }
  };

  const testPost = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/social/linkedin/test-post");
      toast.success(`Test post published to LinkedIn: "${(res.data.title || "").slice(0, 60)}"`);
      invalidate();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    try {
      await api.delete("/admin/social/linkedin");
      toast.success("LinkedIn disconnected");
      invalidate();
    } catch (e) { toast.error(apiError(e)); }
  };

  const callbackUrl = `${window.location.origin}/api/v1/social/linkedin/callback`;
  const expiringSoon = li?.connected && li?.days_left != null && li.days_left <= 7;

  return (
    <div data-testid="linkedin-panel"
      className="mb-6 bg-[#121212] border border-white/10 rounded-xl p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-9 w-9 rounded-lg bg-[#0A66C2]/15 border border-[#0A66C2]/40 flex items-center justify-center shrink-0">
            <Linkedin className="h-4.5 w-4.5 text-[#38BDF8]" />
          </span>
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-white text-base">LinkedIn Company Page</h2>
            <a href={li?.company_url || "https://www.linkedin.com/company/newslayer"} target="_blank" rel="noreferrer"
              className="text-xs text-gray-500 hover:text-[#00E5FF] truncate block">
              linkedin.com/company/newslayer
            </a>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!li ? null : !li.configured ? (
            <span data-testid="linkedin-status-badge" className="font-mono text-[10px] uppercase px-2 py-1 rounded border text-yellow-400 bg-yellow-500/10 border-yellow-500/30">API keys missing</span>
          ) : li.connected ? (
            <span data-testid="linkedin-status-badge" className="font-mono text-[10px] uppercase px-2 py-1 rounded border text-green-400 bg-green-500/10 border-green-500/30">Connected</span>
          ) : (
            <span data-testid="linkedin-status-badge" className="font-mono text-[10px] uppercase px-2 py-1 rounded border text-gray-400 bg-gray-500/10 border-gray-500/30">Not connected</span>
          )}
        </div>
      </div>

      {li?.configured && !li?.connected && (
        <div className="mt-4">
          <p className="text-gray-400 text-xs leading-relaxed max-w-2xl">
            Connect once as a page admin and every auto-posted article also publishes to your LinkedIn page —
            full headline, summary, article link, NewsLayer links and 10+ hashtags per post.
          </p>
          <p className="text-gray-500 text-[11px] mt-2">
            First add this redirect URL in your LinkedIn app (Auth tab → Authorized redirect URLs):{" "}
            <code className="text-[#00E5FF] bg-black/40 px-1.5 py-0.5 rounded break-all">{callbackUrl}</code>
            {" "}(add the same path on https://newslayer.com for production).
          </p>
          <Button onClick={connect} disabled={busy} data-testid="linkedin-connect-button"
            className="mt-3 bg-[#0A66C2] hover:bg-[#0A66C2]/80 text-white">
            <Linkedin className="h-4 w-4 mr-1.5" /> {busy ? "Redirecting…" : "Connect LinkedIn"}
          </Button>
        </div>
      )}

      {li?.connected && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
            <span className="text-gray-400">Company: {" "}
              {li.org_urn
                ? <code className="text-white" data-testid="linkedin-org-urn">{li.org_urn}</code>
                : <span className="text-yellow-400">not set — posts are paused until you add it below</span>}
            </span>
            {li.expires_at && (
              <span className={expiringSoon ? "text-yellow-400" : "text-gray-400"} data-testid="linkedin-expiry">
                Token expires {li.expires_at.slice(0, 10)} ({li.days_left}d left{expiringSoon ? " — reconnect soon" : ""})
              </span>
            )}
            {li.last_post_at && <span className="text-gray-400">Last post {timeAgo(li.last_post_at)}</span>}
          </div>
          {li.last_error && (
            <p className="text-red-400/90 text-xs" data-testid="linkedin-last-error">Last error: {li.last_error}</p>
          )}
          {!li.org_urn && (
            <div className="flex flex-wrap items-center gap-2">
              <Input value={orgInput} onChange={(e) => setOrgInput(e.target.value)}
                placeholder="Company ID, URN, or admin URL (linkedin.com/company/XXXXXXX/admin)"
                data-testid="linkedin-org-input"
                className="max-w-md bg-[#1A1A1A] border-white/15 text-white text-sm" />
              <Button onClick={saveOrg} data-testid="linkedin-org-save"
                className="bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/20">
                Save company
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {li.org_urn && (
              <Button onClick={testPost} disabled={busy} data-testid="linkedin-test-post"
                className="bg-[#0A66C2]/15 text-[#38BDF8] border border-[#0A66C2]/40 hover:bg-[#0A66C2]/25">
                <Send className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Posting…" : "Send test post"}
              </Button>
            )}
            {expiringSoon && (
              <Button onClick={connect} disabled={busy} data-testid="linkedin-reconnect-button"
                className="bg-yellow-500/10 text-yellow-400 border border-yellow-500/40 hover:bg-yellow-500/20">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Reconnect
              </Button>
            )}
            <Button onClick={disconnect} data-testid="linkedin-disconnect"
              className="bg-white/5 text-gray-400 border border-white/15 hover:text-red-400 hover:border-red-500/40">
              <Unplug className="h-3.5 w-3.5 mr-1.5" /> Disconnect
            </Button>
          </div>
        </div>
      )}

      {li && !li.configured && (
        <p className="text-gray-400 text-xs mt-3">
          Add LINKEDIN_CLIENT_ID and LINKEDIN_CLIENT_SECRET to the backend environment to enable LinkedIn auto-posting.
        </p>
      )}
    </div>
  );
};

export default function Social() {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const { data: posts } = useQuery({
    queryKey: ["admin-social-posts"],
    queryFn: async () => (await api.get("/admin/social-posts")).data,
    refetchInterval: 60_000,
  });

  const list = posts || [];
  const sent = list.filter((p: any) => p.x_status === "sent").length;
  const failed = list.filter((p: any) => p.x_status === "failed").length;
  const totalLikes = list.reduce((s: number, p: any) => s + (p.metrics?.likes || 0), 0);

  const refreshMetrics = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/admin/social-posts/refresh-metrics");
      toast.success(res.data.message || `Refreshed ${res.data.updated} posts`);
      queryClient.invalidateQueries({ queryKey: ["admin-social-posts"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setRefreshing(false); }
  };

  return (
    <div data-testid="admin-social-page">
      <PageHeader title="Social Posts"
        sub="Everything auto-posted to X (@NewsLayerApp), Telegram and LinkedIn — breaking stories post instantly, plus a scheduled post every 8 hours"
        actions={
          <Button onClick={refreshMetrics} disabled={refreshing} data-testid="refresh-metrics-button"
            className="bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/20">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Fetching…" : "Refresh Metrics"}
          </Button>
        } />

      <LinkedInPanel />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard label="Posted to X" value={sent} accent="#4ADE80" sub="last 50 shown" />
        <StatCard label="Failed" value={failed} accent={failed ? "#F87171" : "#4ADE80"} />
        <StatCard label="Total Likes" value={totalLikes} accent="#F472B6" sub="across shown posts" />
        <StatCard label="Last Post" value={list[0] ? timeAgo(list[0].created_at) : "—"} accent="#00E5FF" />
      </div>

      <Table headers={["Posted", "Type", "Story", "Likes", "Reposts", "X", "Telegram", "LinkedIn", ""]}>
        {list.map((p: any) => (
          <tr key={p.id} data-testid={`social-post-row-${p.id}`}>
            <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{timeAgo(p.created_at)}</td>
            <td className="px-4 py-3">
              <span className={`inline-block font-mono text-[10px] uppercase px-2 py-0.5 rounded border ${
                p.label === "breaking"
                  ? "text-red-400 bg-red-500/10 border-red-500/30"
                  : "text-gray-400 bg-gray-500/10 border-gray-500/30"}`}>
                {p.label || "breaking"}
              </span>
            </td>
            <td className="px-4 py-3 text-white max-w-[280px]">
              <p className="truncate">{p.title}</p>
              {p.x_error && <p className="text-red-400/80 text-xs truncate mt-0.5">{p.x_error}</p>}
              <p className="text-gray-600 text-[11px] truncate mt-0.5">{(p.hashtags || []).slice(0, 4).join(" ")}</p>
            </td>
            <td className="px-4 py-3">
              <span className="inline-flex items-center gap-1 text-gray-300" data-testid={`social-likes-${p.id}`}>
                <Heart className="h-3.5 w-3.5 text-pink-400" />
                {p.metrics ? p.metrics.likes : "—"}
              </span>
            </td>
            <td className="px-4 py-3">
              <span className="inline-flex items-center gap-1 text-gray-300" data-testid={`social-reposts-${p.id}`}>
                <Repeat2 className="h-3.5 w-3.5 text-emerald-400" />
                {p.metrics ? p.metrics.reposts : "—"}
              </span>
            </td>
            <td className="px-4 py-3"><StatusBadge status={p.x_status} /></td>
            <td className="px-4 py-3"><StatusBadge status={p.telegram_status} /></td>
            <td className="px-4 py-3">
              {p.linkedin_status ? (
                <div>
                  <StatusBadge status={p.linkedin_status} />
                  {p.linkedin_metrics && (
                    <span className="flex items-center gap-2 text-gray-400 text-[11px] mt-1 whitespace-nowrap"
                      data-testid={`linkedin-metrics-${p.id}`}>
                      <span className="inline-flex items-center gap-0.5"><Heart className="h-3 w-3 text-pink-400" />{p.linkedin_metrics.likes}</span>
                      <span className="inline-flex items-center gap-0.5"><MessageCircle className="h-3 w-3 text-cyan-400" />{p.linkedin_metrics.comments}</span>
                      <span className="inline-flex items-center gap-0.5"><Eye className="h-3 w-3 text-gray-500" />{p.linkedin_metrics.impressions}</span>
                    </span>
                  )}
                </div>
              ) : <span className="text-gray-600 text-xs">—</span>}
            </td>
            <td className="px-4 py-3">
              {p.tweet_id && (
                <a href={`https://x.com/NewsLayerApp/status/${p.tweet_id}`} target="_blank" rel="noreferrer"
                  data-testid={`social-post-view-${p.id}`}
                  className="inline-flex items-center gap-1 text-[#00E5FF] hover:underline text-xs whitespace-nowrap">
                  View <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </td>
          </tr>
        ))}
        {list.length === 0 && (
          <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500" data-testid="social-posts-empty">
            No social posts yet — they'll appear here as breaking stories go out
          </td></tr>
        )}
      </Table>
      <p className="text-gray-600 text-xs mt-3">
        X metrics come from the X API; LinkedIn likes, comments and impressions come from the LinkedIn
        share statistics API — both refresh on demand via the button above.
      </p>
    </div>
  );
}
